import { UiLegendBoxItem, UILegendRowType } from '@innovation-os-ng/ui/legend-box';
import { FormatNumPipe } from '@innovation-os-ng/ui/utils';
import { ThemeService } from '@innovation-os-ng/ui/viz-tools-ng';

import { TranslateService } from '@itonics/ngx-translate-simple';

import {
  WorldMapCategory,
  WorldMapCategoryMap,
  WorldMapCountry,
  WorldMapData,
  WorldMapGrowthData,
  WorldMapType
} from '../models/worldmap-model';

import * as d3 from 'd3';
// D3 V7 does not support any more .nest()
// in order to use the old method we need to use d3-collection
import * as d3Collection from 'd3-collection';

export class WorldmapComponentLegends {
  /**
   * STATUS
   */
  public legendsActive: Array<UiLegendBoxItem>;
  public worldmapTypesStatus: {
    [key: string]: boolean;
  } = {};

  /**
   * DATA
   */

  protected _data: Array<WorldMapData>;
  protected _growthData: Array<WorldMapGrowthData>;
  public dataRange: Array<number>;

  /**
   *  Elements
   */
  public countries: Array<WorldMapCountry>;

  /**
   * LEGEND MAP
   */

  public legendMap: {
    [key: string]: UiLegendBoxItem;
  } = {};

  /**
   * COLORS
   */
  public categoricalColors: Array<string>;
  public twoStepColors: Array<string>;
  public mainColor: Array<string>;
  public sequentialColors: Array<string>;
  public complementaryColors: Array<string>;

  /**
   * SIZES
   */
  public circleSizes: Array<number>;

  /**
   * CATEGORIES
   */
  public categoryList: Array<WorldMapCategory>;
  public categoryMap: WorldMapCategoryMap;

  /**
   * Translation
   */
  protected legendTranslation: {
    [key: string]: string;
  };

  /**
   * PIPES
   */
  private numberPipe: FormatNumPipe = new FormatNumPipe();

  constructor(protected readonly translate: TranslateService, protected readonly themeService: ThemeService) {}

  protected getActiveLegends() {
    if (!this.legendMap && !this.worldmapTypesStatus) {
      return;
    }
    this.legendsActive = Object.keys(this.worldmapTypesStatus)
      .filter((key) => this.worldmapTypesStatus[key])
      .map((key) => this.legendMap[key]);
  }

  /**
   * LEGENDS
   */
  protected getLegendPie() {
    const type = WorldMapType.PIE;

    const items =
      this._data && this._data.length
        ? this.categoryList.map((category) => ({
            label: category.label,
            color: this.categoryMap[category.label].color
          }))
        : null;

    this.legendMap[type] = this.buildLegend(type, UILegendRowType.CIRCLE, items);
  }

  protected getLegendChoropleth() {
    const scale = d3.scaleLinear().domain(this.dataRange);
    const numTicks = 4;

    //  We render this legend if the set of data has more than 1 country
    const items =
      this.countries && this.countries.length > 1
        ? new Array(numTicks + 1).fill(null).map((_, i) => {
            const d = (this.dataRange[1] / numTicks) * i;
            return {
              label: i === 0 ? this.numberPipe.transform(this.dataRange[0]) : this.numberPipe.transform(d),
              color: scale.range(this.twoStepColors)(d)
            };
          })
        : null;

    this.legendMap[WorldMapType.CHOROPLETH] = this.buildLegend(WorldMapType.CHOROPLETH, UILegendRowType.BAR, items);
  }

  protected getLegendBubble() {
    const scale = d3
      .scaleLinear()
      .domain(d3.extent(this.circleSizes, (d) => d))
      .range(this.dataRange);

    //  We render this legend if the set of data has more than 1 country
    const items =
      this.countries && this.countries.length > 1
        ? this.circleSizes.map((d, i) => ({
            label: this.numberPipe.transform(scale(d)),
            size: d * 2
          }))
        : null;

    this.legendMap[WorldMapType.BUBBLE] = this.buildLegend(WorldMapType.BUBBLE, UILegendRowType.SIZE, items);
  }

  protected getLegendGrowth() {
    const labels = ['low', ' ', 'high'];

    const items =
      !!this._growthData && !!this._growthData.length
        ? this.sequentialColors.map((color, i) => ({
            label: labels[i],
            color
          }))
        : null;
    this.legendMap[WorldMapType.GROWTH] = this.buildLegend(WorldMapType.GROWTH, UILegendRowType.LINE, items);
  }

  protected buildLegend(
    worldmapType: WorldMapType,
    legendType: UILegendRowType,
    items:
      | Array<{
          label: string;
          color?: string;
          size?: number;
        }>
      | number
  ): UiLegendBoxItem {
    const translation = this.legendTranslation && this.legendTranslation[worldmapType] ? this.legendTranslation[worldmapType] : '';
    return {
      title: translation,
      type: legendType,
      items
    };
  }

  /**
   * CIRCLES, COLORS
   */
  protected getCircleSizes() {
    this.circleSizes = [1, 2, 4, 8, 16];
  }

  /**
   * CATEGORIES
   */
  protected getCategoriesMap(data: Array<WorldMapData>): WorldMapCategoryMap {
    if (!data) {
      return {};
    }

    return d3Collection
      .nest()
      .key((d) => d.category)
      .rollup((v) => ({
        label: v[0].category
      }))
      .object(data);
  }

  protected getCategoriesList(categoryMap): Array<WorldMapCategory> {
    return Object.keys(categoryMap).map((key) => categoryMap[key]);
  }

  /**
   * RANGES
   */
  //  Get data Range for scaling the visuals
  protected getDataRange(data: Array<WorldMapCountry>): Array<number> {
    //  We create the dateRange of the total count of all categories of each country
    return data.length > 0 ? d3.extent(data, (d: WorldMapCountry) => d.count) : [0, 0];
  }

  //  Get data Range SCALED if exists for scaling the visuals
  protected getDataRangeScaled(data: Array<WorldMapCountry>): Array<number> {
    //  We create the dateRange of the total count of all categories of each country
    return data.length > 0 ? d3.extent(data, (d: WorldMapCountry) => d.scaledCount) : [0, 0];
  }

  //  Get data Range for scaling
  protected getGrowthDataRange(data): Array<number> {
    return data.length > 0 ? d3.extent(data, (d) => d) : [0, 0];
  }
}
